import React, { useEffect, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Box,
  Typography,
  Button,
  CircularProgress,
  Paper,
  Stack,
} from "@mui/material";
import {
  LocationOn,
  Event as EventIcon,
  AccessTime,
  TableRestaurant,
  AppRegistration,
} from "@mui/icons-material";
import apiClient from "../../api/apiClient";
import NotFound from "../NotFound";
import { formatDate } from "../../utils/dateUtils";

const EventDetails = () => {
  const { eventslug } = useParams();
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  const isPublicEvent = event?.eventType === "public";
  useEffect(() => {
    const fetchEvent = async () => {
      try {
        const response = await apiClient.get(`/events/slug/${eventslug}`);
        setEvent(response.data);
      } catch (err) {
        if (err.response?.status === 400 || err.response?.status === 404) {
          setError(err.response?.data?.message || "Event not found.");
        } else {
          setError("An unexpected error occurred.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchEvent();
  }, [eventslug]);

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "calc(100vh - 120px)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          bgcolor: "background.default",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <NotFound message={error} />;
  }

  return (
    <Box
      sx={{
        minHeight: "calc(100vh - 120px)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: { xs: "15px", sm: "30px" },
        bgcolor: "#f5f5f5",
      }}
    >
      <Paper
        elevation={3}
        sx={{
          padding: "30px",
          maxWidth: "700px",
          textAlign: "center",
          width: "100%",
          borderRadius: "12px",
          background: "#ffffff",
          boxShadow: "0px 6px 12px rgba(0, 0, 0, 0.15)",
        }}
      >
        {/* Event Logo */}
        {event.logoUrl && (
          <Box
            sx={{
              display: "flex",
              justifyContent: "center",
              marginBottom: "20px",
            }}
          >
            <img
              src={event.logoUrl}
              alt={`${event.name} Logo`}
              style={{
                maxWidth: "250px",
                height: "auto",
                objectFit: "contain",
                borderRadius: "8px",
              }}
            />
          </Box>
        )}

        {/* Event Title */}
        <Typography
          variant="h4"
          sx={{
            fontWeight: "bold",
            fontSize: { xs: "28px", md: "36px" }, // Bigger font for larger screens
            color: "primary.main",
            textAlign: "center",
            letterSpacing: "1.5px",
            animation: "fadeIn 1.2s ease-in-out",
            "@keyframes fadeIn": {
              "0%": { opacity: 0, transform: "translateY(-10px)" },
              "100%": { opacity: 1, transform: "translateY(0)" },
            },
          }}
        >
          Welcome to {event.name}
        </Typography>

        {/* Event Venue & Date */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ marginBottom: "10px", mt: 4 }}
        >
          <LocationOn sx={{ color: "primary.main" }} />
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "16px", md: "20px" },
              color: "text.primary",
            }}
          >
            {event.venue}
          </Typography>
        </Stack>

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ marginBottom: "20px" }}
        >
          <EventIcon sx={{ color: "primary.main" }} />
          <Typography
            variant="h6"
            sx={{
              fontSize: { xs: "16px", md: "20px" },
              color: "text.primary",
            }}
          >
            {formatDate(event.date)}
          </Typography>
        </Stack>

        {/* Event Instructions */}
        <Typography
          variant="body2"
          sx={{
            marginBottom: "30px",
            fontSize: { xs: "14px", md: "16px" },
            color: "text.secondary",
          }}
        >
          {isPublicEvent
            ? "Thank you for joining us! Please register below to secure your place."
            : "Thank you for joining us! Please enter your Employee ID to get your table number."}
        </Typography>

        {/* Register Button */}
        <Button
          variant="contained"
          size="large"
          fullWidth
          sx={{
            fontSize: { xs: "16px", md: "18px" },
            padding: "12px",
            textTransform: "none",
            borderRadius: "8px",
            background: "primary.main",
            maxWidth: { xs: "100%", sm: "300px" },
            fontWeight: "bold",
            transition: "0.3s",
            "&:hover": {
              background: "secondary.main",
              transform: "scale(1.05)",
            },
          }}
          onClick={() => navigate(`/r/${eventslug}`)}
          startIcon={
            isPublicEvent ? (
              <AppRegistration /> // public icon
            ) : (
              <TableRestaurant /> // employee icon
            )
          }
        >
          {isPublicEvent ? "Register Now" : "Get Your Table"}
        </Button>

        {/* Event Duration Hint */}
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="center"
          spacing={1}
          sx={{ marginTop: "20px" }}
        >
          <AccessTime fontSize="small" sx={{ color: "primary.main" }} />
          <Typography
            variant="caption"
            sx={{ fontSize: "14px", fontWeight: 400 }}
          >
            Takes only 5 seconds!
          </Typography>
        </Stack>
      </Paper>
    </Box>
  );
};

export default EventDetails;
